import { Icon } from '@fluentui/react';
import React, { cloneElement, CSSProperties, forwardRef, HTMLAttributes, ReactElement, ReactNode, Ref } from 'react';

import { IComponent, IconName } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { Button } from '../Button/Button';
import { buttonStylesIcon, buttonStylesTag } from '../Button/Button.styles';
import { ILoaderProps, Loader } from '../Loader/Loader';
import { TooltipHost } from '../TooltipHost/TooltipHost';
import { IItemStyles, itemStylesDefault } from './Item.styles';

export interface IItemProps<T extends { [key: string]: any }>
  extends IComponent<IItemStyles>,
    Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  data: T;
  idField?: keyof T;
  labelField?: keyof T;
  secondLabelField?: keyof T;
  titleField?: keyof T;
  classNameField?: keyof T;
  iconNameField?: keyof T;
  styleField?: keyof T;
  iconStyleField?: keyof T;
  hasTooltip?: boolean;
  selected?: boolean;
  active?: boolean;
  nextItemIsActive?: boolean;
  hasClose?: boolean;
  emptyTooltip?: string;
  first?: boolean;
  last?: boolean;
  loading?: boolean;
  loaderProps?: ILoaderProps;
  renderer?: (data: T) => ReactNode | undefined;
  onClick?: (data: T, e: React.MouseEvent<HTMLButtonElement>) => void;
  onClose?: (data: T, e: React.MouseEvent<unknown>) => void;
}

interface IItemClassNames {
  root: string;
  labelButton: string;
  label: string;
  labelText: string;
  secondLabel: string;
  secondLabelText: string;
  icon: string;
  close: string;
}

export const Item = forwardRef(<T extends { [key: string]: any }>(props: IItemProps<T>, ref: Ref<HTMLDivElement>) => {
  const {
    className,
    styles,
    data,
    idField = 'id' as keyof T,
    labelField = 'name' as keyof T,
    secondLabelField,
    titleField,
    classNameField,
    iconStyleField,
    iconNameField,
    styleField,
    hasTooltip = false,
    selected,
    hasClose,
    emptyTooltip = 'No tooltip available',
    first,
    last,
    active,
    nextItemIsActive,
    loading,
    loaderProps,
    renderer,
    onClick,
    onClose,
    ...elementProps
  } = props;
  const classNames = useClassNames<Partial<IItemStyles>, IItemClassNames>(
    'Item',
    itemStylesDefault,
    styles,
    className,
    selected && 'selected',
    active && 'active',
    nextItemIsActive && 'next-item-is-active',
    first && 'first',
    last && 'last',
    loading && 'loading'
  );
  const customClassName = classNameField ? data[classNameField] : '';
  const id = `${data[idField]}`;
  const content = (
    <div
      {...elementProps}
      ref={ref}
      key={id}
      data-id={id}
      className={`${classNames.root}${customClassName ? ` ${customClassName}` : ''}`}
      style={!loading && styleField ? (data[styleField] as unknown as CSSProperties | undefined) : undefined}
      data-test={props['data-test'] || 'item'}
    >
      {loading ? (
        <Loader {...loaderProps} />
      ) : (
        <>
          {cloneElement(
            onClick ? <button onClick={(event) => onClick(data, event)} /> : <div />,
            {
              className: classNames.labelButton,
              title: hasTooltip ? '' : titleField ? (data[titleField] as unknown as string) : '',
            },
            <>
              {iconNameField && data[iconNameField] ? (
                <div
                  className={classNames.icon}
                  style={iconStyleField ? (data[iconStyleField] as unknown as CSSProperties | undefined) : undefined}
                >
                  <Icon iconName={data[iconNameField] as unknown as string | undefined} />
                </div>
              ) : null}
              <div className={classNames.label}>
                <div className={classNames.labelText}>{renderer ? renderer(data) : `${data[labelField]}`}</div>
              </div>
              {secondLabelField && (
                <div className={classNames.secondLabel}>
                  <div className={classNames.secondLabelText}>{`${data[secondLabelField]}`}</div>
                </div>
              )}
            </>
          )}
          {hasClose ? (
            <div className={classNames.close}>
              <Button
                styles={[buttonStylesIcon, buttonStylesTag]}
                iconName={IconName.Cancel}
                onClick={(event) => {
                  if (onClose) onClose(data, event);
                }}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );

  return hasTooltip ? (
    <TooltipHost
      key={String(data[idField])}
      id={String(data[idField])}
      content={String(titleField && data[titleField] ? data[titleField] : emptyTooltip)}
    >
      {content}
    </TooltipHost>
  ) : (
    content
  );
}) as <T extends { [key: string]: any }>(props: IItemProps<T> & { ref?: Ref<HTMLDivElement> }) => ReactElement;
